import { CalendarMonth, FormatListBulleted, Map } from '@mui/icons-material';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import _ from 'lodash';
import qs from 'qs';
import React, { createContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDownloadCsv } from '../../../hooks/useDownloadCsv';
import { useSearchTerm } from '../../../hooks/useSearchTerm';
import { useUserSorts } from '../../../hooks/useUserSorts';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { useGetAllEventTemplate } from '../../../services/react-query/event_templates';
import { EventSortParam } from '../../../services/react-query/events';
import { useUpdateUserSettings } from '../../../services/react-query/users/useUpdateUserSettings';
import { EventToggleBtnStyling, listPageLayoutStyles } from '../../../styles/sxStyling';
import { EventItemQueryParams } from '../../../types/Event';
import { EventViewSetting, UserConfigurableModule } from '../../../types/User';
import { EventColumns, useEventFilters } from '../../../utility/event';
import EventCalendar from '../../common/calenders/EventCalender/EventCalender';
import FilterSidebar from '../../common/filters/FilterSidebar';
import { FilterState } from '../../common/filters/interfaces';
import TemplateModal from '../../common/TemplateModal/TemplateModal';
import EventMap from '../../events/EventMap/EventMap';
import EventTable from '../../events/EventTable/EventTable';
import ListHeader from '../../listHeader/ListHeader';
import ListHeaderActions from '../../listHeader/ListHeaderActions';

export const FilterContext = createContext<FilterState>(undefined);

const getEventListView = (pathname: string): EventViewSetting => {
  if (_.endsWith(pathname, ROUTE_PATHS.EVENTS_CALENDAR)) {
    return EventViewSetting.CALENDAR;
  }
  if (_.endsWith(pathname, ROUTE_PATHS.EVENTS_MAP)) {
    return EventViewSetting.MAP;
  }
  return EventViewSetting.TABLE;
};

const getFilterKey = (view: EventViewSetting): UserConfigurableModule => {
  switch (view) {
    case EventViewSetting.CALENDAR:
      return UserConfigurableModule.EVENTS_CALENDAR;
    default:
      return UserConfigurableModule.EVENTS;
  }
};

const EventsLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { mutate: updateUserSettings } = useUpdateUserSettings();

  const eventListView = getEventListView(location.pathname);

  const { userSorts } = useUserSorts<EventSortParam>(UserConfigurableModule.EVENTS);

  const { data: eventTemplateData } = useGetAllEventTemplate({
    params: { per_page: 0 },
  });

  const eventTemplates = eventTemplateData?.data || [];

  const [showModal, setShowModal] = useState(false);

  const [filterState, setFilterState] = useState<FilterState>(undefined);

  const baseParams = {
    filters: filterState,
    search: useSearchTerm(),
    sort: userSorts,
  };

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: UserConfigurableModule.EVENTS,
  });

  const filterKey = getFilterKey(eventListView);

  const moduleFilters = useEventFilters(eventListView);

  const handleNewEvent = (templateUuid: string | null) => {
    const q: EventItemQueryParams = {};

    if (templateUuid) {
      q.template = templateUuid;
    }

    navigate(`/${ROUTE_PATHS.EVENTS}/new?${qs.stringify(q)}`);
  };

  const handleViewChange = (view: EventViewSetting) => {
    if (view === eventListView) return;

    updateUserSettings({ event_view: view });

    switch (view) {
      case EventViewSetting.CALENDAR:
        navigate(`/${ROUTE_PATHS.EVENTS_CALENDAR}`);
        break;
      case EventViewSetting.MAP:
        navigate(`/${ROUTE_PATHS.EVENTS_MAP}`);
        break;
      default:
        navigate(`/${ROUTE_PATHS.EVENTS_TABLE}`);
    }
  };

  const renderView = () => {
    switch (eventListView) {
      case EventViewSetting.CALENDAR:
        return <EventCalendar />;
      case EventViewSetting.MAP:
        return <EventMap />;
      default:
        return <EventTable />;
    }
  };

  const headerActions = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <ToggleButtonGroup value={eventListView} exclusive size="small" sx={EventToggleBtnStyling}>
        <ToggleButton value={EventViewSetting.TABLE} onClick={() => handleViewChange(EventViewSetting.TABLE)}>
          <FormatListBulleted />
          Table
        </ToggleButton>

        <ToggleButton value={EventViewSetting.CALENDAR} onClick={() => handleViewChange(EventViewSetting.CALENDAR)}>
          <CalendarMonth />
          Calendar
        </ToggleButton>

        <ToggleButton value={EventViewSetting.MAP} onClick={() => handleViewChange(EventViewSetting.MAP)}>
          <Map />
          Map
        </ToggleButton>
      </ToggleButtonGroup>

      <ListHeaderActions
        module={UserConfigurableModule.EVENTS}
        onAdd={() => setShowModal(true)}
        {...(eventListView === EventViewSetting.TABLE && {
          isDownloadingCsv,
          onDownloadCsv: handleDownloadCsv,
          availableColumns: EventColumns.map(({ key, label }) => ({
            key,
            label: label ?? key,
          })),
        })}
      />
    </Box>
  );

  return (
    <>
      <Box>
        <ListHeader title="Events" rightActions={headerActions} />

        <Box sx={listPageLayoutStyles}>
          <FilterSidebar moduleFilters={moduleFilters} module={filterKey} activeFilter={filterState} setActiveFilter={setFilterState} />

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
            }}
          >
            <FilterContext.Provider value={filterState}>{renderView()}</FilterContext.Provider>
          </Box>
        </Box>
      </Box>

      <TemplateModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onCreate={handleNewEvent}
        templates={eventTemplates}
        title="Please choose which event template to use"
      />
    </>
  );
};

export default EventsLayout;
